import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ZoneService from "@services/ZoneService";
import utils from "@utils";
import { v4 as uuid } from "uuid";
import axios from "axios";
import Loader from "../Loader/Loader";
import Modal from "../Modal/Modal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import "./LocationZonesModal.scss";

const LocationZonesModal = ({ show, onHide, zones, locations, onZonesChanged }) => {
	let _confirmationModal = useRef();
	const [locationZones, setLocationZones] = useState([]);
	const [zoneAddValue, setZoneAddValue] = useState("");
	const locationZonesRef = useRef([]);
	const zoneAddInputRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const { lang } = global;

	useEffect(() => {
		const data = [...zones];
		setLocationZones(data);
		locationZonesRef.current = data;
		setLoading(false);
	}, [zones]);

	const handleChange = (e, id) => {
		const { value } = e.target;
		const regEx = /^[a-z\d\s]+$/i;
		let isValid = true;
		if (value === "" || !value.replace(/\s/g, "").length) {
			isValid = false;
		}

		if (value.match(regEx) || value === "") {
			setLocationZones(prevLocationZones =>
				prevLocationZones.map(locationZone => {
					if (locationZone.Id === id) {
						return {
							...locationZone,
							Name: value,
							isValid,
							updated: !locationZone.isNew,
						};
					}
					return locationZone;
				}),
			);
		}
	};

	const handleDelete = id => {
		const onConfirm = () => {
			setLocationZones(prevLocationZones => prevLocationZones.filter(lz => lz.Id !== id));
		};
		if (locations.find(item => item.ZoneId === id) != null) {
			_confirmationModal.openModal({
				title: lang.t("Delete Zone"),
				message: lang.t(
					"This zone is used by some locations. Are you sure you want to delete this zone_",
				),
				confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Zone")}`,
				onConfirm,
			});
		} else {
			setLocationZones(prevLocationZones => prevLocationZones.filter(lz => lz.Id !== id));
		}
	};

	const handleAdd = () => {
		if (zoneAddValue.length && zoneAddValue.replace(/\s/g, "").length) {
			setLocationZones(prevLocationZones => [
				...prevLocationZones,
				{
					Id: uuid(),
					Name: zoneAddValue,
					isNew: true,
				},
			]);
			setZoneAddValue("");
		} else {
			zoneAddInputRef.current.focus();
		}
	};

	const handleCancel = () => {
		setLocationZones(locationZonesRef.current);
		setZoneAddValue("");
		onHide();
	};

	const handleSave = () => {
		const newLocationZones = locationZones.filter(lz => lz.isNew);
		const updatedLocationZones = locationZones.filter(lz => lz.updated);
		const deletedLocationZones = locationZonesRef.current.filter(
			lz => !locationZones.find(lz2 => lz2.Id === lz.Id),
		);

		setLoading(true);

		const promises = [
			...newLocationZones.map(lz =>
				ZoneService.create(lz).then(res => {
					setLocationZones(prevLocationZones =>
						prevLocationZones.map(lz2 => (lz2.Id === lz.Id ? res.data : lz2)),
					);
				}),
			),
			...updatedLocationZones.map(lz =>
				ZoneService.update(lz).then(res => {
					setLocationZones(prevLocationZones =>
						prevLocationZones.map(lz2 => (lz2.Id === lz.Id ? res.data : lz2)),
					);
				}),
			),
			...deletedLocationZones.map(lz =>
				ZoneService.delete(lz).catch(() => {
					setLocationZones(prevLocationZones => [...prevLocationZones, lz]);
				}),
			),
		];

		axios
			.all(promises)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Location Zones saved successfully"),
					title: "Success",
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error saving Location Zones"),
					title: "Error",
				});
				setLocationZones(locationZonesRef.current);
			})
			.finally(() => {
				setLoading(false);
				setZoneAddValue("");
				onZonesChanged();
				onHide();
			});
	};

	const addNewZoneNameChanged = value => {
		const regEx = /^[a-z\d\s]+$/i;
		if (value.match(regEx) || value === "") {
			setZoneAddValue(value);
		}
	};

	const handleAddInputKeyDown = e => {
		if (e.key === "Enter") {
			handleAdd();
		}
	};

	const hasInvalidNames = () => {
		if (locationZones.find(item => item.isValid != null && !item.isValid)) {
			return true;
		}
		return false;
	};
	return (
		<>
			<Modal
				id="location-zone-modal"
				show={show}
				onHide={handleCancel}
				size="md"
			>
				<Modal.Header closeButton>
					<Modal.Title className="text-center">{lang.t("Manage Zones")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div className="col-md-10 col-md-8 col-md-offset-2 col-xs-10 col-xs-offset-1 flex">
							<label htmlFor="location-zone-name">{lang.t("Zone Name")}</label>
						</div>
					</div>
					<div className="row">
						<div className="col-md-8 col-md-offset-2 col-xs-10 col-xs-offset-1 flex">
							<input
								className="form-control margin-right-5"
								type="text"
								id="location-zone-name"
								ref={zoneAddInputRef}
								maxLength="50"
								value={zoneAddValue}
								onChange={e => addNewZoneNameChanged(e.target.value)}
								onKeyDown={handleAddInputKeyDown}
							/>
							<button
								type="button"
								className="btn btn-primary"
								onClick={handleAdd}
							>
								+ {lang.t("Add")}
							</button>
						</div>
					</div>
					<Choose>
						<When condition={loading}>
							<div className="loading-wrapper">
								<Loader active={loading} />
							</div>
						</When>
						<When condition={!loading && locationZones.length === 0}>
							<div className="row text-center text-muted">
								<div className="col-xs-12">No results</div>
							</div>
						</When>
						<Otherwise>
							<hr />
							<For
								each="locationZone"
								of={locationZones}
							>
								<div
									className="row"
									key={locationZone.Id}
								>
									<div className="col-xs-10 col-xs-offset-1 flex margin-bottom-10">
										<input
											className="form-control margin-right-5"
											style={
												locationZone.isValid != null &&
												!locationZone.isValid
													? {
															borderColor: "red",
															backgroundColor: "rgba(255,0,0,0.05)",
														}
													: {}
											}
											type="text"
											value={locationZone.Name}
											maxLength="50"
											onChange={e => handleChange(e, locationZone.Id)}
										/>
										<button
											type="button"
											className="btn btn-primary"
											title={lang.t("Delete")}
											onClick={() => handleDelete(locationZone.Id)}
										>
											<i className="rkf rkf-trash font-size-15" />
										</button>
									</div>
								</div>
							</For>
						</Otherwise>
					</Choose>
				</Modal.Body>
				<If condition={!loading}>
					<Modal.Footer>
						<button
							type="button"
							className="btn btn-link btn-cancel"
							onClick={handleCancel}
						>
							{lang.t("Cancel")}
						</button>
						<button
							type="button"
							disabled={hasInvalidNames()}
							className="btn btn-primary"
							onClick={handleSave}
						>
							{lang.t("Save")}
						</button>
					</Modal.Footer>
				</If>
			</Modal>
			<ConfirmationModal
				ref={ref => {
					_confirmationModal = ref;
				}}
			/>
		</>
	);
};

LocationZonesModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	zones: PropTypes.array.isRequired,
	locations: PropTypes.array.isRequired,
	onZonesChanged: PropTypes.func.isRequired,
};

export default LocationZonesModal;
